import { Button, Popover, Spin, Tooltip } from 'antd';
import React, { forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useState } from 'react';
import Cluster from '../../assets/icons/group_event_timeline.svg';
import Single from '../../assets/icons/single_event_timeline.svg';
import './index.scss';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import { NAMESPACE, DECODED_ACCESS_TOKEN } from '../../constants/system.keys';
import { getLogServicesApi } from '../../api/LogServiceApi';
import { LoadingOutlined } from '@ant-design/icons';
import PlayerServiceComponent from '../PlayerService';
import storageService from '../../services/storage.service';

const MARKER_AMOUNT = 5;

const getListTimeInRange = (startTimeMs, endTimeMs, periodMs = moment.duration(1, 'hour').asMilliseconds()) => {
  const listTime = [];
  let curTime = startTimeMs;
  while (curTime < endTimeMs) {
    listTime.push(curTime);
    curTime += periodMs;
  }
  return listTime;
}

const EventTimeline = forwardRef(({ dataDevice, baseUrl, dataFilterType,
  dataRangerGateway = [],
  widthPercentage = 1,
  listPeriodToGroupMs = [moment.duration(1, 'hour').asMilliseconds()]
}, ref) => {
  const [dateRange, setDateRange] = useState(dataRangerGateway);
  const [dataSummaryLog, setDataSummaryLog] = useState({});
  const [periodIndex, setPeriodIndex] = useState(0);
  const [markers, setMarkers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [barContainerWidth, setBarContainerWidth] = useState(null);
  const { t } = useTranslation();

  useLayoutEffect(() => {
    setBarContainerWidth(document.getElementById('event-timeline-bar-container')?.offsetWidth);
  }, []);

  useEffect(() => {
    if (dataDevice) {
      setIsLoading(true);
      fetchDataSummaryLog(dataDevice?.serial, dataFilterType, dataRangerGateway[0], dataRangerGateway[1]);
    }
  }, [dataRangerGateway])

  useImperativeHandle(ref, () => {
    return {
      refreshTimelineData() {
        setIsLoading(true);
        fetchDataSummaryLog(dataDevice?.serial, dataFilterType, dataRangerGateway[0], dataRangerGateway[1]);
      },
    };
  }, [dataRangerGateway]);

  useEffect(() => {
    return () => {
      setDataSummaryLog({});
    }
  }, []);

  useEffect(() => {
    setDateRange(dataRangerGateway);
    return () => {
      setPeriodIndex(0);
      setMarkers([]);
    }
  }, [dataRangerGateway]);

  useEffect(() => {//onchange dateRange
    setMarkers(Array(MARKER_AMOUNT).fill(0).map((_, index) => {
      const leftPercentage = Math.round(100 / (MARKER_AMOUNT - 1) * index);
      const time = index < MARKER_AMOUNT - 1
        ? dateRange[0] + Math.ceil((dateRange[1] - dateRange[0]) / (MARKER_AMOUNT - 1)) * index
        : dateRange[1];
      return {
        leftPercentage,
        label: moment(time).format("HH:mm:ss"),
      }
    }));
  }, [dateRange]);

  const fetchDataSummaryLog = async (serial, type, fromTime, toTime) => {
    const token = storageService.getInternalStorageByKey(DECODED_ACCESS_TOKEN);
    const namespace = localStorage.getItem(NAMESPACE)
    const dataQuery = {
      serial,
      fromTime: fromTime ? Math.floor(fromTime / 1000) : Math.floor(moment().startOf('day').valueOf() / 1000),
      toTime: toTime ? Math.floor(toTime / 1000) : Math.floor(moment().endOf('day').valueOf() / 1000),
      limit: 300,
      offset: 0,
      isDownload: 0,
      customerNamespace: namespace
    }
    try {
      const dataRes = await getLogServicesApi(baseUrl, token, { namespace }, dataQuery);
      const dataRecordsTemp = _.uniqBy(dataRes?.data?.records ?? [], function (e) {
        return e.timestamp;
      });
      setDataSummaryLog(dataRes?.data ? {
        ...dataRes?.data,
        records: dataRecordsTemp
      } : {});
      setIsLoading(false);
    } catch (error) {
      setDataSummaryLog({});
      setIsLoading(false);
    }
  }

  const eventGroups = useMemo(() => dataSummaryLog?.records?.length && listPeriodToGroupMs?.at(periodIndex)
    ? getListTimeInRange(dateRange[0], dateRange[1], listPeriodToGroupMs?.at(periodIndex)).reduce((groups, time) => {
      const eventsInGroup = dataSummaryLog?.records?.filter(event => event.timestamp * 1000 >= time && event.timestamp * 1000 < time + listPeriodToGroupMs?.at(periodIndex));
      if (eventsInGroup?.length) {
        const lastGroupShowTimestamp = groups.findLast(group => group.showTimestamp);
        const leftPercentage = Math.round((time - dateRange[0]) / (dateRange[1] - dateRange[0]) * 100);

        let showTimestamp = false;
        if (lastGroupShowTimestamp) {
          let minimumDistanceToShowTimestampPx = 78;
          if (lastGroupShowTimestamp?.totalEvent === 1 && eventsInGroup.length === 1)
            minimumDistanceToShowTimestampPx = 48;
          else if (lastGroupShowTimestamp?.totalEvent > 1 && eventsInGroup.length > 1)
            minimumDistanceToShowTimestampPx = 108;

          showTimestamp = barContainerWidth
            ? Math.ceil(barContainerWidth * lastGroupShowTimestamp.leftPercentage / 100) + minimumDistanceToShowTimestampPx
            < Math.ceil(barContainerWidth * leftPercentage / 100)
            : false;
        } else {
          showTimestamp = true;
        }
        return [...groups, {
          objects: eventsInGroup.length === 1 ? eventsInGroup[0].objects : '',
          serial: eventsInGroup.length === 1 ? eventsInGroup[0].serial : '',
          imageUrl: eventsInGroup.length === 1 ? eventsInGroup[0].imageUrl : '',
          videoUrl: eventsInGroup.length === 1 ? eventsInGroup[0].videoUrl : '',
          timestamp: eventsInGroup[0].timestamp * 1000,
          groupStartTime: time,
          totalEvent: eventsInGroup.length,
          leftPercentage,
          widthPercentage: widthPercentage,
          showTimestamp,
        }]
      }
      return [...groups];
    }, [])
    : []
    , [dataSummaryLog, listPeriodToGroupMs, periodIndex, dateRange, widthPercentage, barContainerWidth]);

  const handleClickGroupEvents = (group) => {
    const periodToGroupMs = listPeriodToGroupMs?.[periodIndex];
    setDateRange([group.groupStartTime, group.groupStartTime + periodToGroupMs]);
    if (periodIndex + 1 < listPeriodToGroupMs?.length)
      setPeriodIndex(periodIndex + 1)
  }

  useEffect(() => {
    if (eventGroups.length === 1 && eventGroups[0].totalEvent > 1) {
      if (periodIndex === 0)
        return;
      handleClickGroupEvents(eventGroups[0])
    }
  }, [eventGroups]);

  const handleResetZoom = () => {
    setPeriodIndex(0);
    setDateRange(dataRangerGateway);
  }

  return isLoading ? <Spin indicator={<LoadingOutlined style={{ fontSize: 30, textAlign: 'center' }} spin />} /> : (
    <div className='event-bar-wrapper'>
      {periodIndex > 0 && <Button className="button-reset-zoom" onClick={handleResetZoom}>Reset Zoom</Button>}
      <div className='event-bar-container'>
        <span className='event-date'>{moment(dateRange[0]).format('HH:mm:ss DD-MM-YYYY')}</span>
        <div className='event-bar-main'>
          <div className='bar-container' id='event-timeline-bar-container'>
            <div className='bar-background'></div>
            {markers.map((marker) =>
              <div
                className='bar-marker-item'
                style={{
                  left: `${marker.leftPercentage}%`,
                }}
              >
                <div className='bar-marker-item-header'></div>
                <div className='bar-marker-item-body'></div>
                <div className='bar-marker-item-text'>{marker.label}</div>
              </div>)}
            {eventGroups?.length > 0 && eventGroups.map((group, index) =>
              <Tooltip
                title={
                  group.totalEvent > 1
                    ?
                    <Popover
                      placement="top"
                      title="Group Event Logs"
                      content={
                        <div>
                          <span style={{ display: 'block' }}>{`Group of: ${group.totalEvent} events`}</span>
                          <span style={{ display: 'block' }}>{`From: ${moment(group.groupStartTime).format('HH:mm:ss DD-MM-YYYY')}`}</span>
                          <span style={{ display: 'block' }}>{`To: ${moment(group.groupStartTime + listPeriodToGroupMs[periodIndex]).format('HH:mm:ss DD-MM-YYYY')}`}</span>
                        </div>
                      }>
                      <div
                        onClick={() => handleClickGroupEvents(group)}
                        style={{ width: '100%', height: '100%', display: "flex", alignItems: 'center', justifyContent: 'center', cursor: "pointer" }}>
                        <img style={{ width: '24px', height: '24px' }} src={Cluster} alt='Event-icon' />
                      </div>
                    </Popover>
                    : <Popover
                      placement="top"
                      title="Event Log"
                      content={
                        <div style={{ display: 'flex' }}>
                          <div style={{ marginRight: '20px' }}>
                            {group.videoUrl
                              ? <div className="streaming-video-popover" style={{ width: '190px', position: "relative" }}>
                                <PlayerServiceComponent
                                  type="video"
                                  src={group.videoUrl}
                                />
                              </div>
                              : <img src={group.imageUrl ?? ''} alt='Event' style={{ height: '150px', width: 'auto' }} />}
                          </div>
                          <div>
                            <span style={{ display: 'block', marginBottom: '20px', }}>Action Time: {moment(group.timestamp).format('HH:mm:ss DD-MM-YYYY')}</span>
                            <span style={{ display: 'block', marginBottom: '20px', }}>Serial: {group.serial}</span>
                            <span style={{ display: 'block' }}>Objects: {group.objects}</span>
                          </div>
                        </div>
                      }>
                      <div style={{ width: '100%', height: '100%', display: "flex", alignItems: 'center', justifyContent: 'center' }}>
                        <img style={{ width: '24px', height: '24px' }} src={Single} alt='Event-icon' />
                      </div>
                    </Popover>
                }
                visible={true}
                key={`event-tooltip-item-top-${group.groupStartTime}-${periodIndex}-${index}`}
                overlayClassName='event-tooltip-item-top'
                zIndex={0}
              >
                <Tooltip
                  title={
                    group.totalEvent > 1
                      ? <span>{`${moment(group.groupStartTime).format('HH:mm:ss')} to ${moment(group.groupStartTime + listPeriodToGroupMs[periodIndex]).format('HH:mm:ss')}`}</span>
                      : <span>{moment(group.timestamp).format('HH:mm:ss')}</span>}
                  placement='bottom'
                  overlayClassName='event-tooltip-item-bottom'
                  key={`event-tooltip-item-bottom-${group.groupStartTime}-${periodIndex}-${index}`}
                  visible={group.showTimestamp}
                  zIndex={0}
                >
                  <div
                    key={`event-item-${group.groupStartTime}`}
                    className='event-item'
                    style={{
                      left: `${group.leftPercentage}%`,
                      width: `${group.widthPercentage}%`,
                      background: group.totalEvent > 1 ? "#f15958" : "#fd9113",
                    }}
                  />
                </Tooltip>
              </Tooltip>
            )}
          </div >
        </div>
        <span className='event-date'>{moment(dateRange[1]).format('HH:mm:ss DD-MM-YYYY')}</span>
      </div >
    </div >
  );
});

export default EventTimeline;
